import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { operatorAPI, childrenAPI, caregiversAPI, adminAPI, API, gmailAPI } from '../../lib/api';
import { 
  ArrowLeft, CheckCircle, XCircle, Clock, User, Baby, School, 
  Mail, Phone, MessageSquare, Settings, RefreshCw, Save, Globe,
  Link as LinkIcon, Copy, Loader2, UserPlus, Send, TestTube, X,
  FileText, Sparkles, AlertCircle, Link2, Unlink
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Family Drilldown Page
 * Endpoints used:
 * - GET /api/operator/family/{family_id}
 * - GET /api/operator/family/{family_id}/checklist
 * - GET /api/children/family/{family_id}
 * - GET /api/caregivers (filtered)
 * - GET /api/admin/schools?family_id={family_id}
 * - PUT /api/operator/caregiver/{caregiver_id}/language
 * - POST /api/operator/family/{family_id}/compute-score
 */
export const OperatorFamilyDetail = () => {
  const { familyId } = useParams();
  const [family, setFamily] = useState(null);
  const [children, setChildren] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Invite state
  const [invites, setInvites] = useState([]);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(null);
  
  // Test Email Modal state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailSubject, setTestEmailSubject] = useState('');
  const [testEmailBody, setTestEmailBody] = useState('');
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  
  // Resend Plan state
  const [resendingPlan, setResendingPlan] = useState(false);
  
  // Gmail OAuth state
  const [gmailConnections, setGmailConnections] = useState([]);
  const [loadingGmail, setLoadingGmail] = useState(false);
  const [connectingGmail, setConnectingGmail] = useState(false);
  const [disconnectingId, setDisconnectingId] = useState(null);

  useEffect(() => {
    loadData();
    loadGmailConnections();
  }, [familyId]);
  
  const loadGmailConnections = async () => {
    try {
      setLoadingGmail(true);
      const response = await gmailAPI.getConnections(familyId);
      if (response.data?.ok) {
        setGmailConnections(response.data.connections || []);
      }
    } catch (err) {
      console.log('Gmail connections not available:', err);
      setGmailConnections([]);
    } finally {
      setLoadingGmail(false);
    }
  };
  
  const handleConnectGmail = async () => {
    try {
      setConnectingGmail(true);
      const response = await gmailAPI.startAuth(familyId);
      
      if (response.data?.ok && response.data?.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error(response.data?.error || 'Failed to start Gmail connection');
      }
    } catch (err) {
      console.error('Gmail connect error:', err);
      toast.error('Failed to connect Gmail. Check if Google OAuth is configured.');
    } finally {
      setConnectingGmail(false);
    }
  };
  
  const handleDisconnectGmail = async (connectionId, email) => {
    if (!confirm(`Disconnect ${email} from Gmail sync?`)) return;
    
    try {
      setDisconnectingId(connectionId);
      const response = await gmailAPI.disconnect(connectionId, familyId);
      
      if (response.data?.ok) {
        toast.success(`Disconnected ${email}`);
        loadGmailConnections();
      } else {
        toast.error(response.data?.error || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Gmail disconnect error:', err);
      toast.error('Failed to disconnect Gmail');
    } finally {
      setDisconnectingId(null);
    }
  };

  const loadData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const [familyRes, childrenRes, caregiversRes, schoolsRes, invitesRes] = await Promise.all([
        operatorAPI.getFamily(familyId).catch(e => ({ data: { ok: false, error: e.message } })),
        childrenAPI.getByFamily(familyId).catch(e => ({ data: [] })),
        caregiversAPI.getByFamily(familyId).catch(e => ({ data: [] })),
        adminAPI.getSchools(familyId).catch(e => ({ data: { schools: [] } })),
        fetch(`${API}/operator/family/${familyId}/invites`).then(r => r.json()).catch(e => ({ ok: false, invites: [] })),
      ]);
      
      if (!familyRes.data?.ok && familyRes.data?.error) {
        setError(familyRes.data.error);
      } else {
        setFamily(familyRes.data);
        setChecklist(familyRes.data?.setup_checklist);
      }
      
      setChildren(Array.isArray(childrenRes.data) ? childrenRes.data : childrenRes.data?.children || []);
      
      // Caregivers from family-specific endpoint
      const caregiversData = Array.isArray(caregiversRes.data) ? caregiversRes.data : caregiversRes.data?.caregivers || [];
      setCaregivers(caregiversData);
      
      setSchools(schoolsRes.data?.schools || []);
      setInvites(invitesRes.invites || []);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLanguage = async (caregiverId, language) => {
    try {
      setSaving(true);
      await operatorAPI.updateCaregiverLanguage(caregiverId, language);
      toast.success('Language updated');
      loadData();
    } catch (err) {
      toast.error('Failed to update language');
    } finally {
      setSaving(false);
    }
  };

  const handleRecomputeScore = async () => {
    try {
      setSaving(true);
      await operatorAPI.recomputeScore(familyId);
      toast.success('Score recomputed');
      loadData();
    } catch (err) {
      toast.error('Failed to recompute score');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateInvite = async () => {
    try {
      setGeneratingInvite(true);
      const response = await fetch(`${API}/operator/family/${familyId}/invite`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.ok) {
        // Use current origin for invite URL instead of backend's hardcoded URL
        const inviteUrl = `${window.location.origin}/invite/${data.token}`;
        
        // Try to copy to clipboard
        try {
          await navigator.clipboard.writeText(inviteUrl);
          toast.success('Invite link generated and copied!');
        } catch (clipboardErr) {
          console.warn('Clipboard write failed:', clipboardErr);
          toast.success('Invite link generated! Click the copy button to copy.');
        }
        
        setCopiedInvite(inviteUrl);
        setTimeout(() => setCopiedInvite(null), 5000);
        loadData();
      } else {
        toast.error(data.error || 'Failed to generate invite');
      }
    } catch (err) {
      console.error('Generate invite error:', err);
      toast.error('Failed to generate invite');
    } finally {
      setGeneratingInvite(false);
    }
  };

  // Reminder time edit state
  const [editingReminderTime, setEditingReminderTime] = useState(false);
  const [newReminderTime, setNewReminderTime] = useState('');
  const [savingReminderTime, setSavingReminderTime] = useState(false);

  const handleSaveReminderTime = async () => {
    if (!newReminderTime) return;
    
    setSavingReminderTime(true);
    try {
      const response = await fetch(`${API}/operator/family/${familyId}/reminder-time?reminder_time=${newReminderTime}`, {
        method: 'PUT'
      });
      const data = await response.json();
      
      if (data.ok) {
        toast.success(`Reminder time set to ${newReminderTime} IST`);
        setEditingReminderTime(false);
        loadData();
      } else {
        toast.error(data.error || 'Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update reminder time');
    } finally {
      setSavingReminderTime(false);
    }
  };

  const handleCopyInvite = async (token) => {
    try {
      const inviteUrl = `${window.location.origin}/invite/${token}`;
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedInvite(inviteUrl);
      toast.success('Link copied!');
      setTimeout(() => setCopiedInvite(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  // Test Heritage Email
  const handleTestEmail = async () => {
    if (!testEmailSubject.trim() || !testEmailBody.trim()) {
      toast.error('Please enter both subject and body');
      return;
    }
    
    setTestingEmail(true);
    setTestEmailResult(null);
    
    try {
      const response = await operatorAPI.testHeritageEmail(familyId, {
        subject: testEmailSubject,
        body: testEmailBody
      });
      
      setTestEmailResult(response.data);
      
      if (response.data.ok) {
        toast.success(`Parsed as: ${response.data.card?.type || 'unknown'}`);
      } else {
        toast.error(response.data.error || 'Parsing failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
      setTestEmailResult({ ok: false, error: err.message });
    } finally {
      setTestingEmail(false);
    }
  };

  // Resend Today's Plan
  const handleResendPlan = async () => {
    // Check if caregiver has WhatsApp
    const primaryCaregiver = caregivers.find(c => c.is_primary) || caregivers[0];
    if (!primaryCaregiver?.whatsapp_phone) {
      toast.error('No caregiver with WhatsApp number found');
      return;
    }
    
    setResendingPlan(true);
    
    try {
      const response = await operatorAPI.resendTodaysPlan(familyId);
      
      if (response.data.ok) {
        toast.success(`Plan sent to ${response.data.caregiver_name}! SID: ${response.data.twilio_sid?.slice(0, 10)}...`);
      } else {
        toast.error(response.data.error || 'Resend failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resend failed');
    } finally {
      setResendingPlan(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="family-detail-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="family-detail-error">
        <Link to="/operator/families" className="flex items-center gap-2 text-primary hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Families
        </Link>
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  const checklistItems = [
    { key: 'whatsapp_verified', label: 'WhatsApp Verified', points: 15 },
    { key: 'child_added', label: 'Child Added', points: 10 },
    { key: 'caregiver_added', label: 'Caregiver Added', points: 15 },
    { key: 'caregiver_language', label: 'Caregiver Language Set', points: 10 },
    { key: 'schedule_enabled', label: 'Schedule Enabled', points: 20 },
    { key: 'school_configured', label: 'School Configured', points: 20 },
    { key: 'email_forwarding', label: 'Email Forwarding', points: 10 },
  ];

  return (
    <div data-testid="operator-family-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/operator/families" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {family?.family_name || 'Unnamed Family'}
            </h1>
            <p className="text-sm text-slate-500 font-mono">{familyId}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowTestEmailModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            data-testid="test-email-btn"
          >
            <TestTube className="w-4 h-4" />
            Test Email
          </button>
          <button
            onClick={handleResendPlan}
            disabled={resendingPlan || caregivers.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="resend-plan-btn"
            title={caregivers.length === 0 ? 'No caregiver added' : 'Resend today\'s plan via WhatsApp'}
          >
            {resendingPlan ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Resend Plan
          </button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleRecomputeScore}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            <Settings className="w-4 h-4" />
            Recompute Score
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Setup Checklist */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="setup-checklist">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Setup Score</h2>
              <div className="relative w-16 h-16">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                  <circle
                    cx="32"
                    cy="32"
                    r="28"
                    fill="none"
                    stroke={family?.setup_score >= 60 ? '#22c55e' : family?.setup_score >= 30 ? '#eab308' : '#ef4444'}
                    strokeWidth="6"
                    strokeDasharray={`${(family?.setup_score || 0) * 1.76} 176`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-lg font-bold">
                  {family?.setup_score || 0}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {checklistItems.map((item) => {
                const isComplete = checklist?.[item.key];
                return (
                  <div 
                    key={item.key}
                    className={`flex items-center gap-3 p-2 rounded-lg ${isComplete ? 'bg-green-50' : 'bg-slate-50'}`}
                  >
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-slate-300" />
                    )}
                    <span className={`text-sm ${isComplete ? 'text-green-700' : 'text-slate-500'}`}>
                      {item.label}
                    </span>
                    <span className="ml-auto text-xs text-slate-400">+{item.points}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Family Settings */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6" data-testid="family-settings">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm text-slate-500">Reminder Time (IST)</label>
                {editingReminderTime ? (
                  <div className="flex items-center gap-2 mt-1">
                    <select
                      value={newReminderTime}
                      onChange={(e) => setNewReminderTime(e.target.value)}
                      className="px-3 py-1.5 border border-slate-200 rounded-md text-sm"
                      data-testid="reminder-time-select"
                    >
                      <option value="">Select time</option>
                      <option value="18:00">6:00 PM</option>
                      <option value="18:30">6:30 PM</option>
                      <option value="19:00">7:00 PM</option>
                      <option value="19:30">7:30 PM (Default)</option>
                      <option value="20:00">8:00 PM</option>
                      <option value="20:30">8:30 PM</option>
                      <option value="21:00">9:00 PM</option>
                    </select>
                    <button
                      onClick={handleSaveReminderTime}
                      disabled={!newReminderTime || savingReminderTime}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {savingReminderTime ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingReminderTime(false)}
                      className="px-3 py-1.5 text-slate-600 text-sm hover:bg-slate-100 rounded-md"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{family?.reminder_time || '19:30'} IST</p>
                    <button
                      onClick={() => {
                        setNewReminderTime(family?.reminder_time || '19:30');
                        setEditingReminderTime(true);
                      }}
                      className="text-indigo-600 text-sm hover:underline"
                      data-testid="edit-reminder-time-btn"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Timezone</label>
                <p className="font-medium">{family?.timezone || 'Asia/Kolkata'}</p>
              </div>
              <div>
                <label className="text-sm text-slate-500">WhatsApp Verified</label>
                <p className="font-medium flex items-center gap-2">
                  {family?.whatsapp_verified ? (
                    <><CheckCircle className="w-4 h-4 text-green-500" /> Yes</>
                  ) : (
                    <><XCircle className="w-4 h-4 text-slate-400" /> No</>
                  )}
                </p>
              </div>
              <div>
                <label className="text-sm text-slate-500">Email Forwarding</label>
                <p className="font-medium flex items-center gap-2">
                  {family?.email_forwarding_enabled ? (
                    <><CheckCircle className="w-4 h-4 text-green-500" /> Enabled</>
                  ) : (
                    <><XCircle className="w-4 h-4 text-slate-400" /> Disabled</>
                  )}
                </p>
              </div>
              {family?.parent_primary_email && (
                <div>
                  <label className="text-sm text-slate-500">Parent Email</label>
                  <p className="font-medium text-sm">{family.parent_primary_email}</p>
                </div>
              )}
            </div>
          </div>

          {/* Parent Invites */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6" data-testid="invite-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Parent Invites
              </h2>
              <button
                onClick={handleGenerateInvite}
                disabled={generatingInvite}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
                data-testid="generate-invite-btn"
              >
                {generatingInvite ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <LinkIcon className="w-4 h-4" />
                )}
                Generate Invite
              </button>
            </div>

            {copiedInvite && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Invite link copied to clipboard!
                </p>
                <p className="text-xs text-green-600 mt-1 font-mono break-all">{copiedInvite}</p>
              </div>
            )}

            {invites.length === 0 ? (
              <p className="text-slate-500 text-sm">No invites generated yet. Click "Generate Invite" to create a link for parents to join.</p>
            ) : (
              <div className="space-y-2">
                {invites.slice(0, 5).map((invite) => (
                  <div 
                    key={invite.id} 
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      invite.is_active ? 'bg-green-50' : invite.is_used ? 'bg-blue-50' : 'bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {invite.is_active ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : invite.is_used ? (
                        <CheckCircle className="w-4 h-4 text-blue-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-slate-400" />
                      )}
                      <div>
                        <p className="text-sm font-mono text-slate-700">{invite.token}</p>
                        <p className="text-xs text-slate-500">
                          {invite.is_used ? (
                            <>Used {invite.used_at ? new Date(invite.used_at).toLocaleDateString() : ''}</>
                          ) : invite.is_expired ? (
                            'Expired'
                          ) : (
                            <>Expires {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : ''}</>
                          )}
                        </p>
                      </div>
                    </div>
                    {invite.is_active && (
                      <button
                        onClick={() => handleCopyInvite(invite.token)}
                        className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                        title="Copy invite link"
                      >
                        <Copy className="w-4 h-4 text-green-600" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Gmail Connection */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6" data-testid="gmail-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Gmail Auto-Sync
              </h2>
              <button
                onClick={handleConnectGmail}
                disabled={connectingGmail}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
                data-testid="connect-gmail-btn"
              >
                {connectingGmail ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                Connect Gmail
              </button>
            </div>
            
            {loadingGmail ? (
              <div className="flex items-center gap-2 text-slate-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading connections...
              </div>
            ) : gmailConnections.length > 0 ? (
              <div className="space-y-2">
                {gmailConnections.map((conn) => (
                  <div
                    key={conn.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${conn.status === 'active' ? 'bg-green-500' : 'bg-amber-500'}`} />
                      <div>
                        <p className="font-medium text-slate-900">{conn.email}</p>
                        <p className="text-xs text-slate-500">
                          {conn.status === 'active' ? (
                            conn.last_sync ? `Last sync: ${new Date(conn.last_sync).toLocaleString()}` : 'Connected'
                          ) : (
                            <span className="text-amber-600">{conn.error || 'Needs re-auth'}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDisconnectGmail(conn.id, conn.email)}
                      disabled={disconnectingId === conn.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {disconnectingId === conn.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Unlink className="w-4 h-4" />
                      )}
                      Disconnect
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No Gmail accounts connected. Connect Gmail to auto-sync school emails without forwarding setup.</p>
            )}
          </div>
        </div>

        {/* Children & Caregivers */}
        <div className="lg:col-span-2 space-y-6">
          {/* Children */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="children-section">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Baby className="w-5 h-5" />
              Children ({children.length})
            </h2>
            
            {children.length === 0 ? (
              <p className="text-slate-500 text-sm">No children added yet.</p>
            ) : (
              <div className="space-y-3">
                {children.map((child) => (
                  <div key={child.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                      <Baby className="w-5 h-5 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">
                        {child.first_name} {child.last_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {child.date_of_birth ? `Born: ${new Date(child.date_of_birth).toLocaleDateString()}` : 'DOB not set'}
                        {child.is_primary_school_child && (
                          <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Primary School</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Caregivers */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="caregivers-section">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Caregivers ({caregivers.length})
            </h2>
            
            {caregivers.length === 0 ? (
              <p className="text-slate-500 text-sm">No caregivers added yet.</p>
            ) : (
              <div className="space-y-3">
                {caregivers.map((caregiver) => (
                  <div key={caregiver.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{caregiver.full_name}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                        <span className="capitalize">{caregiver.role}</span>
                        {caregiver.whatsapp_phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {caregiver.whatsapp_phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <select
                        value={caregiver.preferred_language || 'hinglish'}
                        onChange={(e) => handleUpdateLanguage(caregiver.id, e.target.value)}
                        disabled={saving}
                        className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      >
                        <option value="english">English</option>
                        <option value="hinglish">Hinglish</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Schools */}
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="schools-section">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <School className="w-5 h-5" />
              Schools ({schools.length})
            </h2>
            
            {schools.length === 0 ? (
              <p className="text-slate-500 text-sm">No schools configured yet.</p>
            ) : (
              <div className="space-y-3">
                {schools.map((school) => (
                  <div key={school.id} className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900">{school.name}</p>
                    <div className="mt-2 space-y-1 text-sm text-slate-500">
                      {school.email_domains?.length > 0 && (
                        <p className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          Domains: {school.email_domains.join(', ')}
                        </p>
                      )}
                      {school.from_emails?.length > 0 && (
                        <p className="flex items-center gap-2">
                          <MessageSquare className="w-3 h-3" />
                          {school.from_emails.length} sender(s) configured
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Last Error */}
          {family?.last_error_stage && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="last-error">
              <h2 className="text-lg font-semibold text-red-800 mb-2">Last Error</h2>
              <p className="text-red-700">{family.last_error_stage}</p>
            </div>
          )}
        </div>
      </div>

      {/* Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="test-email-modal">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <TestTube className="w-5 h-5 text-purple-600" />
                Test Heritage Email Parser
              </h2>
              <button
                onClick={() => {
                  setShowTestEmailModal(false);
                  setTestEmailResult(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  value={testEmailSubject}
                  onChange={(e) => setTestEmailSubject(e.target.value)}
                  placeholder="New announcement by Teacher in 2025 Class NURSERY B SEL..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="test-email-subject"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Body
                </label>
                <textarea
                  value={testEmailBody}
                  onChange={(e) => setTestEmailBody(e.target.value)}
                  placeholder="Today's update:&#10;&#10;SEL&#10;We talked about feelings and emotions...&#10;&#10;English&#10;Letter recognition - we learned about the letter A..."
                  rows={8}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                  data-testid="test-email-body"
                />
              </div>
              
              <button
                onClick={handleTestEmail}
                disabled={testingEmail}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                data-testid="test-email-submit"
              >
                {testingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Parse & Generate Plan
                  </>
                )}
              </button>
              
              {/* Results */}
              {testEmailResult && (
                <div className={`p-4 rounded-lg ${testEmailResult.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {testEmailResult.ok ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Parsing Successful!</span>
                      </div>
                      
                      {/* Card Info */}
                      <div className="bg-white rounded-lg p-3 border border-green-200">
                        <h4 className="font-medium text-slate-800 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Parsed Card
                        </h4>
                        <div className="mt-2 text-sm space-y-1">
                          <p><span className="text-slate-500">Type:</span> <span className="font-mono bg-blue-100 text-blue-700 px-1 rounded">{testEmailResult.card?.type}</span></p>
                          <p><span className="text-slate-500">Title:</span> {testEmailResult.card?.title}</p>
                          <p><span className="text-slate-500">Summary:</span> {testEmailResult.card?.summary}</p>
                          {testEmailResult.card?.child_name && (
                            <p><span className="text-slate-500">Child:</span> {testEmailResult.card?.child_name}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Daily Plan Preview */}
                      {testEmailResult.daily_plan && (
                        <div className="bg-white rounded-lg p-3 border border-green-200">
                          <h4 className="font-medium text-slate-800 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            Generated Daily Plan
                          </h4>
                          <div className="mt-2 space-y-2">
                            {testEmailResult.daily_plan.school_themes && (
                              <div className="flex flex-wrap gap-1">
                                {testEmailResult.daily_plan.school_themes.map((t, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">{t}</span>
                                ))}
                              </div>
                            )}
                            
                            <div className="text-sm space-y-1">
                              <p className="font-medium text-slate-700">Activities:</p>
                              {testEmailResult.daily_plan.activities?.map((act, i) => (
                                <p key={i} className="text-slate-600 pl-2">• {act.title} ({act.duration_minutes} min)</p>
                              ))}
                            </div>
                            
                            {testEmailResult.daily_plan.read_aloud && (
                              <div className="text-sm">
                                <p className="font-medium text-slate-700">Story:</p>
                                <p className="text-slate-600 pl-2">📖 {testEmailResult.daily_plan.read_aloud.title}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Persistence Status */}
                      <div className="text-sm text-green-700">
                        {testEmailResult.persisted ? (
                          <p className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Saved to DB. Plan ID: {testEmailResult.plan_id?.slice(0, 8)}...
                            {testEmailResult.plan_existing && <span className="text-amber-600 ml-1">(existing plan)</span>}
                          </p>
                        ) : (
                          <p className="text-amber-600">Not persisted (no daily plan generated)</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700">
                      <AlertCircle className="w-5 h-5" />
                      <span>{testEmailResult.error || 'Parsing failed'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorFamilyDetail;
